import { NgTemplateOutlet, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatCard, MatCardHeader, MatCardTitleGroup, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DisplayableFieldResponse } from '@model/ftf-api.model';

@Component({
  selector: 'ip-detail',
  templateUrl: './ip-detail.component.html',
  styleUrl: './ip-detail.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [MatCard, MatCardHeader, MatCardTitleGroup, MatCardTitle, MatTooltip, MatIcon, MatCardContent, NgTemplateOutlet, NgClass],
})
/**
 * Component to display Label / Value pairs.
 *
 * @remarks
 * This component displays information in Label / Value pairs.
 * It sorts the displayable fields and optional can show them in a closable card.
 *
 * @example
 * ```html
 * <ip-detail [detail]="ipDetail" [title]="'IP Details'" (closed)="onDetailClosed()"></ip-detail>
 * ```
 *
 * @property {DisplayableFieldResponse} detail - The detailed information to display.
 * @property {string | null} [title=null] - The title of the detail section.  If not provided only the details are shown.
 * @property {EventEmitter<void>} closed - Event emitter that emits when the details are closed.
 */
export class IpDetailComponent {
  /**
   * The maximum length of a long text field before it causes the row
   * to flip to a vertically stacked label / value.
   */
  LONG_TEXT_LENGTH = 50;

  @Input({ required: true }) detail!: DisplayableFieldResponse;
  @Input() title: string | null = null;
  @Input() closeable = true;
  @Input() onlyKeys: string[] | null = null;
  @Output() closed = new EventEmitter<void>();

  /**
   * Emits the `closed` event to notify that the details view has been closed.
   *
   * @remarks
   * This method should be called when the user closes the details panel.
   */
  onDetailsClose() {
    this.closed.emit();
  }

  /**
   * Returns a sorted array of displayable fields from the `detail` object.
   * The fields are filtered based on the presence of `onlyKeys` (if specified),
   * and then sorted such that fields with long text values (length greater than `LONG_TEXT_LENGTH`)
   * appear after those with shorter text values. Within each group, fields are sorted alphabetically by their key.
   *
   * @returns {Array<{ field: string; value: string; key: string }>} The sorted and filtered displayable fields,
   * or an empty array if `detail` is not defined.
   */
  getSortedDisplayableFields() {
    return this.detail
      ? this.detail.displayable
        .filter((field) => !this.onlyKeys || this.onlyKeys.includes(field.field))
        .sort((a, b) => {
          const aIsLongText = a.value.length > this.LONG_TEXT_LENGTH;
          const bIsLongText = b.value.length > this.LONG_TEXT_LENGTH;
          return aIsLongText === bIsLongText ? a.key.localeCompare(b.key) : aIsLongText ? 1 : -1;
        })
      : [];
  }
}
